<script>
  // Перенос src_ui/web/src/screens/Spawn.tsx — дословно (инлайн-стили строками,
  // SVG-иконки через {@html}, таблички точек в мире по сообщению spawnpins).
  import { onMount, onDestroy } from 'svelte';
  import { nuiPost } from './nui';

  export let char;
  export let onBack = () => {};
  export let onConfirm = () => {};

  const C = {
    gold: '#e0a83e', goldHi: '#ecb44c', goldLo: '#c97e2c', onGold: '#241a06',
    panelA: 'rgba(22,18,14,0.94)', panelB: 'rgba(13,11,9,0.93)',
    line: 'rgba(255,255,255,0.09)', ink2: 'rgba(255,255,255,0.62)', ink3: 'rgba(255,255,255,0.4)',
  };
  const panelBg = `linear-gradient(135deg, ${C.panelA}, ${C.panelB})`;
  const goldGrad = `linear-gradient(135deg, ${C.goldHi}, ${C.goldLo})`;

  const IC = {
    pin: '<path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11z"/><circle cx="12" cy="10" r="2.6"/>',
    home: '<path d="M4 11l8-6 8 6"/><path d="M6 10v9h12v-9"/><path d="M10 19v-5h4v5"/>',
    shield: '<path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z"/>',
    lock: '<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>',
  };

  // useMemo [char] из React: список точек считается от char и нужен уже при
  // инициализации sel — поэтому обычная функция + реактивное присвоение
  const buildOpts = (c) => [
    { key: 'last', name: 'Последняя точка', badge: 'Последний выход', on: true, sub: c.lastPlace || 'Где вышел в прошлый раз', pos: c.pos, icon: IC.pin },
    c.home
      ? { key: 'home', name: 'Дом', badge: 'Ваш дом', on: true, sub: `${c.home.name} · ${c.home.addr}`, pos: c.homePos, icon: IC.home }
      : { key: 'home', name: 'Дом', badge: 'Нет жилья', on: false, sub: 'Купи дом у риелтора — появится точка', icon: IC.home },
    c.org
      ? { key: 'org', name: c.org.role, badge: 'Организация', on: true, sub: `${c.org.name} · ${c.org.role}`, pos: c.orgPos, icon: IC.shield }
      : { key: 'org', name: 'Организация', badge: 'Вне фракции', on: false, sub: 'Ты не состоишь во фракции или на работе', icon: IC.shield },
  ];
  $: opts = buildOpts(char);

  let sel = Math.max(0, buildOpts(char).findIndex((o) => o.on));

  // Таблички точек прямо в мире: игра проецирует верх столба в координаты
  // экрана (0..1) и присылает их сюда, здесь они превращаются в подписи.
  let pins = [];
  const onMsg = (e) => {
    const d = e.data;
    if (d && d.type === 'spawnpins') pins = Array.isArray(d.pins) ? d.pins : [];
  };
  onMount(() => { window.addEventListener('message', onMsg); });
  onDestroy(() => { window.removeEventListener('message', onMsg); });

  // все доступные точки отмечаем в мире (столб + подпись) — useEffect [opts]
  let _pOpts = null;
  $: if (opts !== _pOpts) {
    _pOpts = opts;
    const pts = opts
      .filter((o) => o.on && o.pos)
      .map((o) => ({ key: o.key, name: o.name, x: o.pos.x, y: o.pos.y, z: o.pos.z }));
    nuiPost('spawnPoints', { points: pts, active: opts[sel]?.key ?? '' });
  }

  // камера летит к выбранной точке — useEffect [sel]
  let _pSel = -1;
  $: if (sel !== _pSel) {
    _pSel = sel;
    const o = opts[sel];
    if (o?.on && o.pos) nuiPost('spawnView', { ...o.pos, key: o.key });
  }

  $: cur = opts[sel];
  const cycle = (dir) => {
    const avail = opts.map((o, i) => (o.on ? i : -1)).filter((i) => i >= 0);
    if (!avail.length) return;
    const p = avail.indexOf(sel);
    sel = avail[(p + dir + avail.length) % avail.length];
  };
  const confirm = () => { if (cur?.on) onConfirm(cur.pos ?? { x: 0, y: 0, z: 0, h: 0 }, cur.key); };

  const onKey = (e) => {
    if (e.key === 'ArrowLeft') cycle(-1);
    else if (e.key === 'ArrowRight') cycle(1);
    else if (e.key === 'Enter') confirm();
    else if (e.key === 'Escape') onBack();
  };

  const pinName = (k) => opts.find((o) => o.key === k)?.name ?? '';
  const pinBadge = (k) => opts.find((o) => o.key === k)?.badge ?? '';
</script>

<svelte:window on:keydown={onKey} />

<div style="position:fixed;inset:0;font-family:'Manrope',sans-serif;animation:lcFadeIn 0.5s ease">
  <div style="position:absolute;inset:0;pointer-events:none;background:radial-gradient(120% 95% at 50% 50%, transparent 40%, rgba(5,6,9,0.55) 74%, rgba(4,5,7,0.88) 100%)"></div>

  <!-- таблички точек прямо в мире — над световыми столбами -->
  <div style="position:fixed;inset:0;pointer-events:none;z-index:4">
    {#each pins as p (p.key)}
      <div style="position:absolute;left:{p.x * 100}%;top:{p.y * 100}%;transform:translate(-50%, -100%);display:flex;flex-direction:column;align-items:center;opacity:{p.active ? 1 : 0.62};transition:left 70ms linear, top 70ms linear, opacity 0.25s;will-change:left, top">
        <div style="display:flex;align-items:center;gap:9px;padding:9px 14px;border-radius:14px;background:{p.active
            ? 'linear-gradient(135deg, rgba(40,32,18,0.96), rgba(18,14,10,0.94))'
            : 'linear-gradient(135deg, rgba(20,20,22,0.9), rgba(12,12,14,0.88))'};border:1px solid {p.active ? 'rgba(224,168,62,0.7)' : 'rgba(255,255,255,0.12)'};box-shadow:{p.active ? '0 14px 40px rgba(0,0,0,0.6), 0 0 26px rgba(224,168,62,0.22)' : '0 12px 30px rgba(0,0,0,0.5)'};white-space:nowrap">
          <span style="width:9px;height:9px;border-radius:50%;flex-shrink:0;background:{p.active ? goldGrad : 'rgba(255,255,255,0.35)'};box-shadow:{p.active ? '0 0 12px rgba(224,168,62,0.8)' : 'none'}"></span>
          <span style="display:flex;flex-direction:column;gap:2px;text-align:left">
            <span style="font:800 15px 'Manrope';color:#fff;line-height:1">{pinName(p.key)}</span>
            <span style="font:700 10px 'Manrope';letter-spacing:0.14em;text-transform:uppercase;color:{p.active ? C.gold : C.ink3};line-height:1">
              {pinBadge(p.key)}{p.dist > 0 ? ` · ${p.dist} м` : ''}
            </span>
          </span>
        </div>
        <!-- хвост от таблички вниз — продолжение светового столба -->
        <div style="width:2px;height:26px;background:{p.active
            ? 'linear-gradient(to bottom, rgba(224,168,62,0.95), rgba(224,168,62,0))'
            : 'linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(255,255,255,0))'}"></div>
      </div>
    {/each}
  </div>

  <!-- заголовок -->
  <div style="position:absolute;top:44px;left:0;right:0;text-align:center;pointer-events:none">
    <div style="font:700 12px 'Manrope';letter-spacing:0.26em;text-transform:uppercase;color:{C.gold};margin-bottom:11px">Выбор точки появления</div>
    <div style="font:800 38px 'Unbounded';color:#fff;line-height:1">Где появиться</div>
    <div style="margin-top:11px;font:600 15px 'Manrope';color:{C.ink2}">Играем за <b style="color:#fff;font-weight:800">{char.identity.firstName} {char.identity.lastName}</b></div>
  </div>

  <!-- переключатель точки: стрелки по бокам и название выбранной -->
  <div style="position:absolute;left:50%;bottom:150px;transform:translateX(-50%);display:flex;align-items:center;gap:34px">
    <div on:click={() => cycle(-1)} style="width:108px;height:108px;border-radius:50%;background:{panelBg};border:1px solid {C.line};display:flex;align-items:center;justify-content:center;cursor:pointer;color:#fff"><svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg></div>
    <div style="min-width:380px;display:flex;flex-direction:column;align-items:center;gap:9px">
      <div style="font:800 30px 'Unbounded';color:{cur?.on ? '#fff' : C.ink3};line-height:1;white-space:nowrap">
        {cur?.name}
      </div>
      <div style="display:flex;align-items:center;gap:9px;font:700 11px 'Manrope';letter-spacing:0.18em;text-transform:uppercase;color:{cur?.on ? C.gold : C.ink3}">
        {#if !cur?.on}<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">{@html IC.lock}</svg>{/if}
        {cur?.badge}
      </div>
      <div style="font:600 14px 'Manrope';color:{C.ink2};text-align:center;min-height:20px">{cur?.sub}</div>
    </div>
    <div on:click={() => cycle(1)} style="width:108px;height:108px;border-radius:50%;background:{panelBg};border:1px solid {C.line};display:flex;align-items:center;justify-content:center;cursor:pointer;color:#fff"><svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg></div>
  </div>

  <!-- кнопка -->
  <div style="position:absolute;left:50%;bottom:56px;transform:translateX(-50%)">
    <button on:click={confirm} disabled={!cur?.on} style="padding:19px 58px;border-radius:15px;border:none;cursor:{cur?.on ? 'pointer' : 'default'};opacity:{cur?.on ? 1 : 0.4};background:{goldGrad};color:{C.onGold};font:700 16px 'Unbounded';letter-spacing:0.08em;text-transform:uppercase;box-shadow:0 14px 38px rgba(224,168,62,0.42)">Заспавниться</button>
  </div>

  <!-- назад -->
  <div on:click={onBack} style="position:absolute;left:56px;bottom:56px;display:flex;align-items:center;gap:10px;padding:15px 22px;border-radius:14px;background:rgba(255,255,255,0.05);border:1px solid {C.line};color:{C.ink2};font:700 13.5px 'Manrope';cursor:pointer">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg> К персонажам
  </div>

  <!-- подсказки -->
  <div style="position:absolute;right:56px;bottom:58px;display:flex;align-items:center;gap:18px">
    {#each [['← →', 'Выбор'], ['Enter', 'Подтвердить'], ['Esc', 'Назад']] as [k, t] (k)}
      <div style="display:flex;align-items:center;gap:8px;font:600 12.5px 'Manrope';color:{C.ink3}">
        <span style="display:inline-flex;align-items:center;justify-content:center;min-width:26px;height:26px;padding:0 7px;border-radius:7px;background:rgba(255,255,255,0.07);border:1px solid {C.line};color:{C.ink2};font:700 11px 'Manrope'">{k}</span>{t}
      </div>
    {/each}
  </div>
</div>
